/**
 * Read models over a `PatientState`: the numbers the patient's app, the practice list, the coach
 * prompt and the triage flags all show. Pure; `now` is always an argument.
 */

import {
  FEVER_C,
  FEVER_WATCH_C,
  FLAG_WINDOW_MS,
  HYPOTHERMIA_C,
  INACTIVE_DAYS,
  INTAKE_LOW_ML,
  MISSED_ORAL_DAYS,
  MODERATE_GRADE,
  NADIR_END_DAY,
  NADIR_START_DAY,
  SEVERE_GRADE,
  WEIGHT_LOSS_PERCENT,
  WEIGHT_LOSS_WINDOW_DAYS,
} from './thresholds.js';
import { dayKey, daysBetween, lastDays, localHour, shiftDay, weekdayShort } from './time.js';
import { SYMPTOM_LABEL, GRADE_LABEL, trackFor } from './tracks.js';
import type {
  CareGoal,
  EventSource,
  GradedSymptom,
  Medication,
  MetricDay,
  MetricKind,
  PatientState,
  PerformanceEntry,
  SymptomCode,
  SymptomReport,
  TempReading,
  TreatmentCycle,
} from './types.js';

/* ------------------------------------------------------------- temperature */

export type TempClass = 'low' | 'normal' | 'watch' | 'fever';

export const TEMP_CLASS_LABEL: Readonly<Record<TempClass, string>> = {
  low: 'Below normal',
  normal: 'Normal',
  watch: 'Slightly raised',
  fever: 'Fever',
};

/**
 * Where a temperature sits. The bands are the same for everyone; what changes with the track is
 * what a fever MEANS — on treatment it is an emergency, and `flags` is where that is decided.
 */
export function classifyTemp(celsius: number): TempClass {
  if (celsius >= FEVER_C) return 'fever';
  if (celsius >= FEVER_WATCH_C) return 'watch';
  if (celsius < HYPOTHERMIA_C) return 'low';
  return 'normal';
}

export function latestTemperature(state: PatientState): TempReading | null {
  return state.readings.length > 0 ? (state.readings[state.readings.length - 1] as TempReading) : null;
}

/** The last 7 days, one entry each, with the highest temperature that day (or null). Oldest first. */
export function temperatureTrend(state: PatientState, now: number): Array<{ dayKey: string; label: string; celsius: number | null; at: number | null }> {
  return lastDays(dayKey(now, state.profile.tzOffsetMinutes), 7).map((k) => {
    const same = state.readings.filter((r) => r.dayKey === k);
    if (same.length === 0) return { dayKey: k, label: weekdayShort(k), celsius: null, at: null };
    // The highest of the day, not the newest: a fever that settled by bedtime still happened.
    const peak = same.reduce((a, b) => (b.celsius > a.celsius ? b : a));
    return { dayKey: k, label: weekdayShort(k), celsius: peak.celsius, at: peak.at };
  });
}

/* ------------------------------------------------------------------ cycles */

export interface CyclePosition {
  cycle: TreatmentCycle;
  /** Day 1 is the day of the dose, as the clinic counts. */
  day: number;
  /** Whether this is the window when the white count is expected to be at its lowest. */
  nadir: boolean;
}

/** Where the patient is in their treatment cycle today, or null if no cycle has been recorded. */
export function cyclePosition(state: PatientState, now: number): CyclePosition | null {
  const today = dayKey(now, state.profile.tzOffsetMinutes);
  const started = state.cycles.filter((c) => c.startDayKey <= today);
  const cycle = started[started.length - 1];
  if (!cycle) return null;
  const day = daysBetween(cycle.startDayKey, today) + 1;
  return { cycle, day, nadir: day >= NADIR_START_DAY && day <= NADIR_END_DAY };
}

/* ----------------------------------------------------------------- progress */

export interface WeeklyProgress {
  /** Temperatures logged in the last 7 days, against the days-a-week the plan asks for. */
  temperature: { done: number; target: number; onTrack: boolean };
  /** Symptom checks logged in the last 7 days. */
  symptomChecks: { done: number; target: number };
  /** Doses taken over doses scheduled, last 7 days. */
  adherence: { percent: number; daysComplete: number; taken: number; scheduled: number };
  steps: { daysComplete: number; target: number; todayCount: number; todayGoal: number | null };
}

export const DEFAULT_TEMPERATURE_DAYS = 7;

export function weeklyProgress(state: PatientState, now: number): WeeklyProgress {
  const tz = state.profile.tzOffsetMinutes;
  const today = dayKey(now, tz);
  const days = lastDays(today, 7);
  const daySet = new Set(days);

  const tempGoal = state.goals.find((g): g is Extract<CareGoal, { kind: 'daily-temperature' }> => g.kind === 'daily-temperature');
  const tempTarget = tempGoal?.daysPerWeek ?? DEFAULT_TEMPERATURE_DAYS;
  const tempDone = new Set(state.readings.filter((r) => daySet.has(r.dayKey)).map((r) => r.dayKey)).size;

  const checkGoal = state.goals.find((g): g is Extract<CareGoal, { kind: 'symptom-check' }> => g.kind === 'symptom-check');
  const checkDone = new Set(state.symptomReports.filter((r) => daySet.has(r.dayKey)).map((r) => r.dayKey)).size;

  const stepGoal = state.goals.find((g): g is Extract<CareGoal, { kind: 'steps' }> => g.kind === 'steps') ?? null;
  const stepDays = stepGoal ? state.steps.filter((s) => daySet.has(s.dayKey) && s.count >= stepGoal.target).length : 0;
  const todayCount = state.steps.find((s) => s.dayKey === today)?.count ?? 0;

  let scheduled = 0;
  let taken = 0;
  let daysComplete = 0;
  for (const d of days) {
    // Days before enrollment are not missed doses; only days the plan existed are counted.
    if (d < state.profile.memberSince) continue;
    let dayScheduled = 0;
    let dayTaken = 0;
    for (const med of state.medications) {
      dayScheduled += med.times.length;
      dayTaken += state.takes.filter((t) => t.dayKey === d && t.medicationId === med.id).length;
    }
    // Today only counts the doses whose time has passed, so a morning check-in is not "missed" at 8am.
    if (d === today) {
      const hour = localHour(now, tz);
      dayScheduled = 0;
      dayTaken = 0;
      for (const med of state.medications) {
        med.times.forEach((t, slot) => {
          if (Number(t.slice(0, 2)) <= hour) {
            dayScheduled += 1;
            if (state.takes.some((x) => x.dayKey === d && x.medicationId === med.id && x.slot === slot)) dayTaken += 1;
          }
        });
      }
    }
    scheduled += dayScheduled;
    taken += dayTaken;
    if (dayScheduled > 0 && dayTaken === dayScheduled) daysComplete += 1;
  }
  const percent = scheduled === 0 ? 100 : Math.round((taken / scheduled) * 100);

  return {
    temperature: { done: tempDone, target: tempTarget, onTrack: tempDone >= Math.min(tempTarget, days.indexOf(today) + 1) },
    symptomChecks: { done: checkDone, target: checkGoal?.daysPerWeek ?? 7 },
    adherence: { percent, daysComplete, taken, scheduled },
    steps: { daysComplete: stepDays, target: 7, todayCount, todayGoal: stepGoal?.target ?? null },
  };
}

export interface TodayDose {
  medication: Medication;
  slot: number;
  time: string;
  taken: boolean;
}

/** Every dose scheduled today, in clock order, with whether it has been taken. */
export function todayDoses(state: PatientState, now: number): TodayDose[] {
  const day = dayKey(now, state.profile.tzOffsetMinutes);
  const out: TodayDose[] = [];
  for (const med of state.medications) {
    med.times.forEach((time, slot) => {
      out.push({ medication: med, slot, time, taken: state.takes.some((t) => t.dayKey === day && t.medicationId === med.id && t.slot === slot) });
    });
  }
  return out.sort((a, b) => a.time.localeCompare(b.time));
}

/* ------------------------------------------------------------------ metrics */

/** The newest day held for a metric, or null. Rows are ordered by day, so the last match wins. */
export function metricDay(state: PatientState, kind: MetricKind): MetricDay | null {
  for (let i = state.metrics.length - 1; i >= 0; i--) {
    const m = state.metrics[i] as MetricDay;
    if (m.kind === kind) return m;
  }
  return null;
}

/** The last `n` days of one metric, oldest first, days without a value omitted. */
export function metricDays(state: PatientState, kind: MetricKind, now: number, n = 7): MetricDay[] {
  const days = new Set(lastDays(dayKey(now, state.profile.tzOffsetMinutes), n));
  return state.metrics.filter((m) => m.kind === kind && days.has(m.dayKey));
}

/** The most recent value of each metric kind. */
export function latestMetrics(state: PatientState): Partial<Record<MetricKind, { value: number; at: number }>> {
  const out: Partial<Record<MetricKind, { value: number; at: number }>> = {};
  for (const m of state.metrics) {
    const cur = out[m.kind];
    if (!cur || m.lastAt >= cur.at) out[m.kind] = { value: m.last, at: m.lastAt };
  }
  return out;
}

/** Today's total for a metric the patient tops up through the day — fluids, trips to the toilet. */
export function todayTotal(state: PatientState, kind: MetricKind, now: number): number | null {
  const day = dayKey(now, state.profile.tzOffsetMinutes);
  const row = state.metrics.find((m) => m.kind === kind && m.dayKey === day);
  // These are entered as a running total, so the largest number of the day is the day's answer.
  return row ? row.max : null;
}

export interface WeightTrend {
  latest: number;
  at: number;
  /** Pounds since the previous weighed day. Negative is a loss. */
  dayDeltaLbs: number | null;
  /** Percent of body weight lost since the oldest weight in the window. Positive is a loss. */
  lossPercent: number | null;
  /** Over how many days that loss happened. */
  overDays: number | null;
}

/**
 * Weight, read for loss rather than gain. In oncology the number that matters is what has gone:
 * five per cent in a month is the point at which nutrition becomes part of the treatment plan.
 */
export function weightTrend(state: PatientState): WeightTrend | null {
  const rows = state.metrics.filter((m) => m.kind === 'weight-lbs');
  const newest = rows[rows.length - 1];
  if (!newest) return null;
  const before = rows.filter((r) => r.dayKey < newest.dayKey);
  const prev = before[before.length - 1] ?? null;
  const windowStart = shiftDay(newest.dayKey, -WEIGHT_LOSS_WINDOW_DAYS);
  const base = before.find((r) => r.dayKey >= windowStart) ?? null;
  const round1 = (n: number) => Math.round(n * 10) / 10;
  return {
    latest: newest.last,
    at: newest.lastAt,
    dayDeltaLbs: prev && daysBetween(prev.dayKey, newest.dayKey) <= 3 ? round1(newest.last - prev.last) : null,
    lossPercent: base && base.last > 0 ? round1(((base.last - newest.last) / base.last) * 100) : null,
    overDays: base ? daysBetween(base.dayKey, newest.dayKey) : null,
  };
}

export interface PulseReading {
  value: number;
  at: number;
  source: EventSource;
  manual: boolean;
}

export function latestPulse(state: PatientState): PulseReading | null {
  const row = metricDay(state, 'heart-rate');
  if (!row) return null;
  return { value: row.last, at: row.lastAt, source: row.source ?? 'manual', manual: row.manual === true };
}

export function latestPerformance(state: PatientState): PerformanceEntry | null {
  return state.performance.length > 0 ? (state.performance[state.performance.length - 1] as PerformanceEntry) : null;
}

/* ----------------------------------------------------------------- symptoms */

export interface ReportedSymptoms {
  at: number;
  symptoms: GradedSymptom[];
  note?: string;
  /** Whether it came with a temperature, which tells the care team how to read it. */
  withTemperature: boolean;
}

/** Everything the patient has said they felt lately, from checks and from temperatures alike. */
export function recentSymptoms(state: PatientState, now: number, windowMs = FLAG_WINDOW_MS): ReportedSymptoms[] {
  const out: ReportedSymptoms[] = [];
  for (const r of state.symptomReports as SymptomReport[]) {
    if (now - r.at > windowMs) continue;
    out.push({ at: r.at, symptoms: [...r.symptoms], ...(r.note ? { note: r.note } : {}), withTemperature: false });
  }
  for (const r of state.readings) {
    if (now - r.at > windowMs) continue;
    if (r.symptoms.length > 0) out.push({ at: r.at, symptoms: [...r.symptoms], withTemperature: true });
  }
  return out.sort((a, b) => b.at - a.at);
}

/** The worst grade reported for a symptom lately, or 0. */
export function worstGrade(reported: readonly ReportedSymptoms[], code: SymptomCode): number {
  let worst = 0;
  for (const r of reported) for (const s of r.symptoms) if (s.code === code && s.grade > worst) worst = s.grade;
  return worst;
}

export function symptomLabel(s: SymptomCode): string {
  return SYMPTOM_LABEL[s];
}

export function gradeLabel(grade: number): string {
  return GRADE_LABEL[Math.max(0, Math.min(4, Math.round(grade)))] as string;
}

/** "Diarrhoea (severe)" — how a graded symptom is named wherever it is shown. */
export function describeSymptom(s: GradedSymptom): string {
  return `${symptomLabel(s.code)} (${gradeLabel(s.grade).toLowerCase()})`;
}

/* -------------------------------------------------------------------- flags */

export type FlagLevel = 'urgent' | 'attention' | 'info';

export interface PatientFlag {
  level: FlagLevel;
  code:
    | 'neutropenic-fever'
    | 'fever'
    | 'hypothermia'
    | 'possible-irae'
    | 'severe-symptom'
    | 'symptom-report'
    | 'dehydration-risk'
    | 'weight-loss'
    | 'missed-oral-therapy'
    | 'missed-meds'
    | 'performance-decline'
    | 'no-temperature'
    | 'inactive';
  text: string;
  at: number;
}

/** Symptoms that are an emergency at severe, whatever the track. */
const RED_FLAG_SYMPTOMS: readonly SymptomCode[] = ['breathlessness', 'bleeding', 'confusion', 'vomiting', 'swallowing'];
/** The immune-related ones. On immunotherapy these are organs, not side effects. */
const IRAE_SYMPTOMS: readonly SymptomCode[] = ['diarrhea', 'breathlessness', 'cough'];

/**
 * What the care team should see about this patient right now. Ordered urgent → info. Every flag
 * names the fact behind it, because a flag without a reason is a flag nobody can clear.
 *
 * Three rules hold across all of them. Nothing older than 48 hours raises anything, because a care
 * team cannot act on last month. No device-sourced number alone is urgent. And a fever on active
 * treatment is urgent BY CONSTRUCTION — it is not weighed against anything else, not softened by
 * how well the patient says they feel, and not something the coach is allowed to discuss instead.
 */
export function flags(state: PatientState, now: number): PatientFlag[] {
  const out: PatientFlag[] = [];
  const tz = state.profile.tzOffsetMinutes;
  const today = dayKey(now, tz);
  const track = trackFor(state.profile.treatments);
  const fresh = (at: number) => now - at < FLAG_WINDOW_MS;
  const reported = recentSymptoms(state, now);

  // 1. Temperature. The whole reason this product exists.
  const hot = [...state.readings].reverse().find((r) => fresh(r.at) && r.celsius >= FEVER_WATCH_C) ?? null;
  if (hot) {
    const cls = classifyTemp(hot.celsius);
    const position = cyclePosition(state, now);
    const where = position && track.hasCycles ? ` on day ${position.day} of cycle ${position.cycle.number}` : '';
    if (cls === 'fever' && track.feverIsEmergency) {
      out.push({
        level: 'urgent',
        code: 'neutropenic-fever',
        text: `Temperature ${hot.celsius.toFixed(1)} °C${where}. Fever on treatment — treat as febrile neutropenia until proven otherwise.`,
        at: hot.at,
      });
    } else {
      out.push({
        level: cls === 'fever' ? 'attention' : 'attention',
        code: 'fever',
        text: `Temperature ${hot.celsius.toFixed(1)} °C${where}.`,
        at: hot.at,
      });
    }
  }
  const cold = [...state.readings].reverse().find((r) => fresh(r.at) && r.celsius < HYPOTHERMIA_C) ?? null;
  if (cold && track.feverIsEmergency) {
    out.push({ level: 'urgent', code: 'hypothermia', text: `Temperature ${cold.celsius.toFixed(1)} °C, below ${HYPOTHERMIA_C} °C, on treatment.`, at: cold.at });
  }

  // 2. Immune-related events. On immunotherapy these escalate over days and steroids stop them.
  if (track.id === 'immunotherapy') {
    const irae = reported.find((r) => r.symptoms.some((s) => IRAE_SYMPTOMS.includes(s.code) && s.grade >= MODERATE_GRADE));
    if (irae) {
      const named = irae.symptoms.filter((s) => IRAE_SYMPTOMS.includes(s.code) && s.grade >= MODERATE_GRADE).map(describeSymptom);
      out.push({ level: 'urgent', code: 'possible-irae', text: `${named.join(' and ')} on immunotherapy — possible immune-related event.`, at: irae.at });
    }
  }

  // 3. Severe symptoms on any track.
  const severe = reported.find((r) => r.symptoms.some((s) => s.grade >= SEVERE_GRADE));
  if (severe) {
    const worst = severe.symptoms.filter((s) => s.grade >= SEVERE_GRADE);
    const red = worst.some((s) => RED_FLAG_SYMPTOMS.includes(s.code)) || worst.some((s) => s.grade === 4);
    out.push({ level: red ? 'urgent' : 'attention', code: 'severe-symptom', text: `Reported ${worst.map(describeSymptom).join(', ')}.`, at: severe.at });
  }

  // 4. Drying out: the pair that puts people on a drip, and neither half says it alone.
  const losing = reported.find((r) => r.symptoms.some((s) => (s.code === 'vomiting' || s.code === 'diarrhea') && s.grade >= MODERATE_GRADE));
  const intake = todayTotal(state, 'intake-ml', now);
  if (losing && intake !== null && intake < INTAKE_LOW_ML) {
    out.push({ level: 'attention', code: 'dehydration-risk', text: `Losing fluids and only ${intake} ml taken today.`, at: losing.at });
  }

  // 5. Weight going the wrong way.
  const weight = weightTrend(state);
  if (weight && weight.lossPercent !== null && weight.lossPercent >= WEIGHT_LOSS_PERCENT) {
    out.push({
      level: 'attention',
      code: 'weight-loss',
      text: `Weight down ${weight.lossPercent}% over ${weight.overDays} days, now ${weight.latest} lb.`,
      at: weight.at,
    });
  }

  // 6. Medications. An oral anti-cancer therapy missed is not the same as a missed anti-emetic.
  const oral = state.medications.filter((m) => m.oralTherapy);
  if (oral.length > 0) {
    const days = lastDays(today, MISSED_ORAL_DAYS + 1).slice(0, MISSED_ORAL_DAYS).filter((d) => d >= state.profile.memberSince);
    const missed = days.filter((d) => oral.some((m) => m.times.some((_, slot) => !state.takes.some((t) => t.dayKey === d && t.medicationId === m.id && t.slot === slot))));
    if (days.length === MISSED_ORAL_DAYS && missed.length === MISSED_ORAL_DAYS) {
      out.push({ level: 'attention', code: 'missed-oral-therapy', text: `${oral.map((m) => m.name).join(', ')} not fully ticked off on either of the last ${MISSED_ORAL_DAYS} days.`, at: now });
    }
  }
  const supportive = state.medications.filter((m) => !m.oralTherapy);
  if (supportive.length > 0) {
    const days = lastDays(today, 3).slice(0, 2).filter((d) => d >= state.profile.memberSince);
    const missed = days.filter((d) => supportive.some((m) => m.times.some((_, slot) => !state.takes.some((t) => t.dayKey === d && t.medicationId === m.id && t.slot === slot))));
    if (days.length === 2 && missed.length === 2) {
      out.push({ level: 'attention', code: 'missed-meds', text: 'Missed at least one supportive dose on each of the last two days.', at: now });
    }
  }

  // 7. Losing ground: an ECOG that has got worse is how a decline shows up before anything else.
  const perf = latestPerformance(state);
  if (perf && fresh(perf.at) && perf.score >= 3) {
    out.push({ level: 'attention', code: 'performance-decline', text: `Reported spending most of the day in bed or a chair (ECOG ${perf.score}).`, at: perf.at });
  }

  // 8. Silence.
  const lastTemp = latestTemperature(state);
  const sinceTemp = lastTemp ? Math.floor((now - lastTemp.at) / 86_400_000) : null;
  if (track.feverIsEmergency) {
    if (!lastTemp && daysSince(state.profile.memberSince, today) >= 2) {
      out.push({ level: 'attention', code: 'no-temperature', text: 'No temperature logged since enrollment, on active treatment.', at: now });
    } else if (sinceTemp !== null && sinceTemp >= 3) {
      out.push({ level: 'attention', code: 'no-temperature', text: `No temperature for ${sinceTemp} days, on active treatment.`, at: now });
    }
  }
  const lastActivity = lastLoggedAt(state);
  if (lastActivity !== null && now - lastActivity >= INACTIVE_DAYS * 86_400_000) {
    out.push({ level: 'info', code: 'inactive', text: `Nothing logged for ${Math.floor((now - lastActivity) / 86_400_000)} days.`, at: now });
  }
  return out;
}

/** When this patient last did anything at all. */
export function lastLoggedAt(state: PatientState): number | null {
  const times = [
    ...state.readings.map((r) => r.at),
    ...state.symptomReports.map((r) => r.at),
    ...state.takes.map((t) => t.at),
    ...state.metrics.map((m) => m.lastAt),
    ...state.performance.map((p) => p.at),
  ];
  return times.length > 0 ? Math.max(...times) : null;
}

function daysSince(fromKey: string, toKey: string): number {
  const p = (k: string) => {
    const [y, m, d] = k.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((p(toKey) - p(fromKey)) / 86_400_000);
}

/** Overall engagement for the practice list: 0..100 from adherence, checks and streak. */
export function engagementScore(state: PatientState, now: number): number {
  const p = weeklyProgress(state, now);
  const temp = p.temperature.target === 0 ? 1 : Math.min(1, p.temperature.done / p.temperature.target);
  const checks = p.symptomChecks.target === 0 ? 1 : Math.min(1, p.symptomChecks.done / p.symptomChecks.target);
  const adherence = p.adherence.percent / 100;
  const streak = Math.min(1, state.streak.current / 7);
  return Math.round((temp * 0.3 + checks * 0.2 + adherence * 0.35 + streak * 0.15) * 100);
}
